import { z } from 'zod';
import { IsoDateTime, Uuid } from './common.js';
/**
 * Custom and personal fields (module `custom-fields`, docs/modules/custom-fields.md, ADR 0009). Owner: task S3.
 *
 * The field-type registry itself (pure, shared by server, portal and kiosk) lives in `../fields/registry.ts`,
 * written by S3 to the `FieldTypeSpec` interface below.
 */
/** personal = stored per member of a repeat group; sign_in / sign_out = asked at the kiosk, stored per visit. */
export const FieldScope = z.enum(['personal', 'sign_in', 'sign_out']);
/** A type key from the registry. Built-in keys are in BUILTIN_FIELD_TYPES; modules may add more. */
export const FieldTypeKey = z.string().regex(/^[a-z][a-z0-9_]{1,40}$/);
export const BUILTIN_FIELD_TYPES = [
    'text',
    'long_text',
    'number',
    'email',
    'phone',
    'date',
    'select',
    'multi_select',
    'yes_no',
    'signature',
    'acknowledge',
    'host',
];
/**
 * The widgets every front end must be able to draw. A module-added field type reuses one of these,
 * so the portal and the kiosk can show it without a new app build.
 */
export const FieldWidget = z.enum([
    'text',
    'textarea',
    'number',
    'email',
    'phone',
    'date',
    'select',
    'multi_select',
    'yes_no',
    'signature',
    'acknowledge',
    'member_picker',
]);
/** Personal fields every repeat group has; stored as member columns, not as fields rows. They count towards the limit of 15. */
export const BUILTIN_PERSONAL_FIELDS = ['fullName', 'email', 'phone', 'role'];
export const MAX_PERSONAL_FIELDS = 15;
export const MAX_STARRED_FIELDS = 4;
export const FieldStatus = z.enum(['active', 'archived']);
export const Field = z.object({
    id: Uuid,
    scope: FieldScope,
    /** sign_in / sign_out fields belong to a site; null for personal fields. */
    siteId: Uuid.nullable(),
    /** personal fields belong to one repeat group; null for sign_in / sign_out fields. */
    groupId: Uuid.nullable(),
    /** sign_in / sign_out: the groups that are asked this question at this site. Empty for personal. */
    groupIds: z.array(Uuid),
    typeKey: FieldTypeKey,
    label: z.string(),
    helpText: z.string(),
    required: z.boolean(),
    /** Encrypted at rest; reading or changing values needs groups.manage_sensitive. */
    sensitive: z.boolean(),
    /** Shown on member cards / the today board (max 4 per site+scope or per group). */
    starred: z.boolean(),
    sortOrder: z.number().int(),
    /** Type-specific settings, validated by the type's configSchema. */
    config: z.record(z.string(), z.unknown()),
    status: FieldStatus,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const FieldCreate = z.object({
    scope: FieldScope,
    siteId: Uuid.optional(),
    groupId: Uuid.optional(),
    groupIds: z.array(Uuid).max(200).optional(),
    typeKey: FieldTypeKey,
    label: z.string().trim().min(1).max(120),
    helpText: z.string().trim().max(300).optional(),
    required: z.boolean().optional(),
    sensitive: z.boolean().optional(),
    starred: z.boolean().optional(),
    config: z.record(z.string(), z.unknown()).optional(),
});
/** scope, siteId, groupId and typeKey can't change. */
export const FieldUpdate = z.object({
    groupIds: z.array(Uuid).max(200).optional(),
    label: z.string().trim().min(1).max(120).optional(),
    helpText: z.string().trim().max(300).optional(),
    required: z.boolean().optional(),
    sensitive: z.boolean().optional(),
    starred: z.boolean().optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    status: FieldStatus.optional(),
});
export const FieldListQuery = z.object({
    scope: FieldScope,
    siteId: Uuid.optional(),
    groupId: Uuid.optional(),
    status: z.enum(['active', 'archived', 'all']).default('active'),
});
/** PUT /fields/order: all active field ids of one list (one site+scope, or one group's personal fields), in order. */
export const FieldOrder = z.object({ fieldIds: z.array(Uuid).min(1).max(200) });
/** An answer as sent by the kiosk or the portal. The value is checked by the field type's valueSchema. */
export const FieldAnswer = z.object({ fieldId: Uuid, value: z.unknown() });
/** A stored value as returned by the API. Sensitive values are replaced by `redacted: true` without the permission. */
export const FieldValueOut = z.object({
    fieldId: Uuid,
    label: z.string(),
    typeKey: FieldTypeKey,
    value: z.unknown().optional(),
    /** Plain-text form for lists, exports and badges (FieldTypeSpec.toText). */
    text: z.string().optional(),
    redacted: z.boolean().optional(),
});
/** How a front end should draw the input (ADR 0009). Pure hints; the widget decides the component. */
export const FieldRenderHints = z.object({
    web: z.object({ input: z.string(), multiline: z.boolean().optional(), autoComplete: z.string().optional() }),
    kiosk: z.object({
        keyboard: z.enum(['default', 'email-address', 'phone-pad', 'numeric', 'decimal-pad']),
        multiline: z.boolean().optional(),
        autoCapitalize: z.enum(['none', 'sentences', 'words']).optional(),
    }),
});
/** How the value is written in CSV / Excel exports (the text itself comes from toText). */
export const FieldExportKind = z.enum(['text', 'number', 'date', 'boolean', 'list']);
/** GET /fields/types: what the portal needs to offer and configure each type. */
export const FieldTypeInfo = z.object({
    key: FieldTypeKey,
    module: z.string(),
    labelKey: z.string(),
    widget: FieldWidget,
    scopes: z.array(FieldScope),
    canBeSensitive: z.boolean(),
    canBeStarred: z.boolean(),
    /** JSON Schema of the type's config, for the portal's config form. */
    configJsonSchema: z.record(z.string(), z.unknown()),
    hints: FieldRenderHints.optional(),
    exportKind: FieldExportKind.optional(),
});
/** The field as the kiosk needs it (inside KioskStep config). */
export const KioskField = Field.pick({
    id: true,
    scope: true,
    typeKey: true,
    label: true,
    helpText: true,
    required: true,
    sensitive: true,
    config: true,
    sortOrder: true,
}).extend({ widget: FieldWidget });
//# sourceMappingURL=fields.js.map